import { motion } from "framer-motion";
import {
    Building2,
    UtensilsCrossed,
    Dumbbell,
    Home,
    KeyRound,
    Briefcase,
    ArrowUpRight,
} from "lucide-react";
import { useI18n } from "../../lib/i18n";

const icons = [Building2, UtensilsCrossed, Dumbbell, KeyRound, Home, Briefcase];

/* Cinematic hover images per industry (dark, premium) */
const hoverImages = [
    // Hotels — luxury lobby / pool at night
    "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1600&q=80",
    // Restaurants — moody premium restaurant
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1600&q=80",
    // Gyms — dark training studio
    "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1600&q=80",
    // Real Estate — modern architectural interior
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
    // Luxury Villas — pool at sunset
    "https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1600&q=80",
    // Professional Services — modern executive office
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
];

const cardVariant = {
    hidden: { opacity: 0, y: 30 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.85,
            delay: i * 0.05,
            ease: [0.16, 1, 0.3, 1],
        },
    }),
};

export default function Industries() {
    const { t } = useI18n();
    const s = t.industries;
    return (
        <section
            id="industries"
            className="section relative bg-[#050505]"
            data-testid="industries-section"
        >
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-24">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[1.02]">
                            {s.titlePre}
                            <span className="italic font-extralight text-[#E55B13]">
                                {s.titleAccent}
                            </span>
                            {s.titlePost}
                        </h2>
                    </div>
                    <p className="font-body text-white/55 max-w-sm text-base leading-relaxed">
                        {s.lede}
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    data-testid="industries-grid"
                >
                    {s.cards.map((it, i) => {
                        const Icon = icons[i] || Building2;
                        const n = String(i + 1).padStart(2, "0");
                        const bgImage = hoverImages[i];
                        return (
                            <motion.a
                                key={i}
                                href="#pricing"
                                custom={i}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={cardVariant}
                                className="industry-card group relative rounded-[28px] border border-white/[0.08] bg-gradient-to-b from-white/[0.03] to-transparent p-8 md:p-10 min-h-[280px] flex flex-col justify-between card-lift overflow-hidden isolate"
                                data-testid={`industry-card-${n}`}
                            >
                                {/* Cinematic hover image */}
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 z-0 opacity-0 scale-[1.06] group-hover:opacity-100 group-hover:scale-100 transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                                    style={{
                                        backgroundImage: `url(${bgImage})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                    }}
                                />
                                {/* Dark + orange gradient overlay for readability */}
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 z-[1] opacity-0 group-hover:opacity-100 transition-opacity duration-[900ms] ease-out"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, rgba(5,5,5,0.55) 0%, rgba(5,5,5,0.7) 55%, rgba(5,5,5,0.9) 100%), radial-gradient(80% 60% at 100% 0%, rgba(229,91,19,0.30), transparent 60%)",
                                    }}
                                />
                                {/* Soft orange glow around card on hover */}
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute -inset-4 z-[-1] opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-[36px]"
                                    style={{
                                        boxShadow:
                                            "0 30px 90px -25px rgba(229,91,19,0.35)",
                                    }}
                                />

                                <div className="relative z-10 flex items-start justify-between">
                                    <div className="w-12 h-12 rounded-2xl bg-white/[0.05] border border-white/10 flex items-center justify-center text-white/85 group-hover:text-[#E55B13] group-hover:border-[#E55B13]/50 group-hover:bg-black/40 backdrop-blur-sm transition-colors duration-500">
                                        <Icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <span className="font-mono text-[11px] tracking-[0.28em] text-white/40 group-hover:text-white/70 transition-colors">
                                        {n}
                                    </span>
                                </div>

                                <div className="relative z-10 mt-14">
                                    <h3 className="font-heading text-2xl md:text-[26px] font-medium tracking-[-0.02em] text-white leading-snug drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]">
                                        {it.title}
                                    </h3>
                                    <div className="mt-2 font-heading text-lg md:text-xl font-light italic text-[#E55B13] tracking-[-0.01em]">
                                        {it.promise}
                                    </div>
                                    <p className="mt-4 font-body text-[14px] text-white/70 group-hover:text-white/85 leading-relaxed max-w-xs transition-colors">
                                        {it.line}
                                    </p>

                                    <div className="mt-6 inline-flex items-center gap-2 text-white/80 group-hover:text-[#E55B13] transition-colors">
                                        <span className="font-mono text-[11px] tracking-[0.22em] uppercase">
                                            {s.seePlans}
                                        </span>
                                        <ArrowUpRight
                                            size={14}
                                            className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                                        />
                                    </div>
                                </div>
                            </motion.a>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.2 }}
                    className="mt-12 flex flex-wrap items-center justify-between gap-6 pt-8 border-t border-white/[0.06] font-mono text-[11px] uppercase tracking-[0.24em] text-white/45"
                >
                    <span>{s.footerLead}</span>
                    <a
                        href="#contact"
                        className="text-white hover:text-[#E55B13] transition-colors"
                        data-testid="industries-contact-link"
                    >
                        {s.footerCta}
                    </a>
                </motion.div>
            </div>
        </section>
    );
}
